import { useEffect, useRef } from 'react'

import { Routes, Route, BrowserRouter, useLocation, useNavigate } from 'react-router-dom'

import ProductSelection from '../pages/auth/productSelection'
import ResetPassword from '../pages/auth/resetPassword/ResetPassword'
import BranchSelection from '../pages/branchSelection'
import { clearAllData } from '../utils'
// Run BEFORE React mounts the route tree. Without this, `AuthProtected`
// sees no token on the first render and bounces a remote-login landing
// to /login before the URL scrub takes effect.
import { captureFromUrl } from '../utils/remoteLogin'
captureFromUrl()

import { publicRoutes, authProtectedRoutes } from './allRoutes'
import { AuthProtected } from './authProtectedRoutes'
import { ProfileCompletionGuard } from './profileCompletionGuard'
import { PublicProtected } from './publicRoutes'

import PermissionGuard from '@/common/components/PermissionGuard'
import VerticalLayout from '@/common/layouts/MainLayout'
import NonAuthLayout from '@/common/layouts/NonAuthLayout'
import LiveWall from '@/pages/liveWall'

const ForceLogoutHandler = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const handledRef = useRef(false)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('force') === 'true' && !handledRef.current) {
      handledRef.current = true
      clearAllData()
      navigate('/login', { replace: true })
    }
  }, [location.search, navigate])

  return null
}

const Index = () => {
  return (
    <BrowserRouter>
      <ForceLogoutHandler />
      <Routes>
        {/* Fully public (auth-free) routes — reachable without a session
            token. Intended for shared wall displays / status pages that
            anonymous viewers can open from a generated link. The
            `:token` is the opaque share token minted by
            POST /facilities/{uuid}/share-tokens — NOT the facility
            uuid — so an attacker who reverse-engineers one URL can't
            pivot to other facilities. */}
        <Route path="/live-wall" element={<LiveWall />} />
        <Route path="/live-wall/:token" element={<LiveWall />} />

        <Route>
          {publicRoutes.map((route, idx) => (
            <Route
              path={route.path}
              element={
                <PublicProtected>
                  <NonAuthLayout>{route.component}</NonAuthLayout>
                </PublicProtected>
              }
              key={idx}
              exact={route.exact || false}
            />
          ))}
        </Route>

        {/* Post-login but pre-dashboard routes (no sidebar/topbar). */}
        <Route
          path="/product-selection"
          element={
            <AuthProtected>
              <ProductSelection />
            </AuthProtected>
          }
        />
        <Route
          path="/branch-selection"
          element={
            <AuthProtected>
              <ProfileCompletionGuard>
                <BranchSelection />
              </ProfileCompletionGuard>
            </AuthProtected>
          }
        />
        <Route
          path="/reset-password"
          element={
            <AuthProtected>
              <ResetPassword />
            </AuthProtected>
          }
        />
        <Route
          element={
            <AuthProtected>
              <ProfileCompletionGuard>
                <VerticalLayout />
              </ProfileCompletionGuard>
            </AuthProtected>
          }
        >
          {authProtectedRoutes?.map((route, idx) => (
            <Route
              path={route.path}
              element={
                <PermissionGuard
                  moduleId={route.requiredModule}
                  permissionKeys={route.permissionKeys}
                  requiresCompanyType={route.requiresCompanyType}
                >
                  {route.component}
                </PermissionGuard>
              }
              key={idx}
              exact={route.exact || false}
            />
          ))}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export default Index
